import React, { useCallback, useEffect, useRef, useState } from 'react';

// Fullscreen photo lightbox with two viewing modes:
//   • Fit  — the whole (full-resolution) image scaled to fit the screen.
//   • 100% — true native pixels for pixel-peeping; the image can extend past the
//            screen and is panned by dragging (or scrolling).
// The +/- buttons, the slider, and Ctrl/⌘+wheel zoom set any level in between.
// `src` is the full-resolution image; `placeholder` (optional) shows instantly
// while the full-res file downloads.
const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
const MIN_ZOOM = 0.05;
const MAX_ZOOM = 8;

export default function PhotoViewer({ src, placeholder, name, downloadHref, onClose }) {
  const stageRef = useRef(null);
  const dragRef = useRef(null);
  const [nat, setNat] = useState({ w: 0, h: 0 });
  const [loaded, setLoaded] = useState(false);
  const [fit, setFit] = useState(true);
  const [zoom, setZoom] = useState(1); // multiple of native pixels; only used when !fit

  // Esc closes; lock body scroll while open.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === '0' || e.key.toLowerCase() === 'f') { setFit(true); }
      else if (e.key === '1') { setFit(false); setZoom(1); }
    };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const fitScale = (() => {
    const st = stageRef.current;
    if (!st || !nat.w || !nat.h) return 1;
    return Math.min(st.clientWidth / nat.w, st.clientHeight / nat.h);
  })();
  const pct = Math.round((fit ? fitScale : zoom) * 100);

  const onImgLoad = (e) => {
    setNat({ w: e.target.naturalWidth, h: e.target.naturalHeight });
    setLoaded(true);
  };

  // Set an explicit zoom, anchoring on the stage center, and leave fit mode.
  const applyZoom = useCallback((next) => {
    const st = stageRef.current;
    const z = clamp(next, Math.min(MIN_ZOOM, fitScale), MAX_ZOOM);
    if (st && nat.w) {
      const prevZ = fit ? fitScale : zoom;
      const cx = st.scrollLeft + st.clientWidth / 2;
      const cy = st.scrollTop + st.clientHeight / 2;
      const ratio = z / (prevZ || 1);
      requestAnimationFrame(() => {
        st.scrollLeft = cx * ratio - st.clientWidth / 2;
        st.scrollTop = cy * ratio - st.clientHeight / 2;
      });
    }
    setFit(false);
    setZoom(z);
  }, [fit, fitScale, zoom, nat.w]);

  const toggle = () => {
    if (fit) applyZoom(1);
    else setFit(true);
  };

  // Drag to pan when zoomed past the viewport.
  const onPointerDown = (e) => {
    const st = stageRef.current;
    if (!st) return;
    dragRef.current = { x: e.clientX, y: e.clientY, sl: st.scrollLeft, st: st.scrollTop, moved: false };
    st.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    const d = dragRef.current;
    const st = stageRef.current;
    if (!d || !st) return;
    st.scrollLeft = d.sl - (e.clientX - d.x);
    st.scrollTop = d.st - (e.clientY - d.y);
    if (Math.abs(e.clientX - d.x) + Math.abs(e.clientY - d.y) > 3) d.moved = true;
  };
  const onPointerUp = () => { dragRef.current = null; };

  const onWheel = (e) => {
    if (!(e.ctrlKey || e.metaKey)) return; // plain scroll pans; ⌘/Ctrl zooms
    e.preventDefault();
    const cur = fit ? fitScale : zoom;
    applyZoom(cur * (e.deltaY < 0 ? 1.15 : 0.87));
  };

  const dispW = nat.w ? Math.round(nat.w * zoom) : 0;
  const zoomed = !fit;

  return (
    <div className="cs-pv-overlay" role="dialog" aria-label={`Photo viewer — ${name || ''}`}>
      <div className="cs-pv-bar">
        <span className="cs-pv-name" title={name}>{name}</span>
        <div className="cs-pv-tools">
          <button type="button" className={`cs-pv-btn ${fit ? 'on' : ''}`} onClick={() => setFit(true)} title="Fit the whole image to the screen (F)">Fit</button>
          <button type="button" className={`cs-pv-btn ${!fit && Math.abs(zoom - 1) < 0.001 ? 'on' : ''}`} onClick={() => applyZoom(1)} title="Actual pixels — 100% (1)">100%</button>
          <button type="button" className="cs-pv-btn" onClick={() => applyZoom((fit ? fitScale : zoom) * 0.8)} title="Zoom out">−</button>
          <input
            className="cs-pv-slider"
            type="range"
            min={Math.round(Math.min(MIN_ZOOM, fitScale) * 100)}
            max={MAX_ZOOM * 100}
            value={Math.min(MAX_ZOOM * 100, Math.max(1, pct))}
            onChange={(e) => applyZoom(Number(e.target.value) / 100)}
            aria-label="Zoom"
          />
          <button type="button" className="cs-pv-btn" onClick={() => applyZoom((fit ? fitScale : zoom) * 1.25)} title="Zoom in">+</button>
          <span className="cs-pv-pct">{pct}%</span>
          {nat.w ? <span className="cs-pv-dim">{nat.w}×{nat.h}</span> : null}
          {downloadHref ? (
            <a className="cs-pv-btn" href={downloadHref} target="_blank" rel="noreferrer" title="Open the full-resolution original">Original ↗</a>
          ) : null}
          <button type="button" className="cs-pv-btn cs-pv-close" onClick={onClose} title="Close (Esc)">✕</button>
        </div>
      </div>
      <div
        ref={stageRef}
        className={`cs-pv-stage ${fit ? 'is-fit' : 'is-zoom'}`}
        onPointerDown={zoomed ? onPointerDown : undefined}
        onPointerMove={zoomed ? onPointerMove : undefined}
        onPointerUp={onPointerUp}
        onWheel={onWheel}
        onDoubleClick={toggle}
      >
        {!loaded && (
          <div className="cs-pv-loading">
            {placeholder ? <img src={placeholder} alt="" className="cs-pv-placeholder" /> : null}
            <span className="cs-spinner" />
          </div>
        )}
        <img
          src={src}
          alt={name}
          onLoad={onImgLoad}
          draggable={false}
          className="cs-pv-img"
          style={
            fit
              ? { maxWidth: '100%', maxHeight: '100%', opacity: loaded ? 1 : 0 }
              : { width: dispW ? `${dispW}px` : 'auto', maxWidth: 'none', maxHeight: 'none', opacity: loaded ? 1 : 0 }
          }
        />
      </div>
    </div>
  );
}
